// Disabling checks for "no-shadow" because the potential problems only occur if we reference
// outside values from within enum declarations, which we generally do not do.
// For an example, see https://typescript-eslint.io/rules/no-shadow/#why-does-the-rule-report-on-enum-members-that-share-the-same-name-as-a-variable-in-a-parent-scope
/* eslint-disable @typescript-eslint/no-shadow */

export namespace FormsConstants {
    /**
     * Key-prefix used for accessing localStorage and file storage
     */
    export const KEY_FORMS = 'PaceForms';

    /**
     * Which kind of form is this? Value of constant is used to identify form based on formId in the store.
     * Numbers in the 800s are from Astoria, the rest in the 300s are from Cosima.
     */
    export enum FORMTYPE {
        MAINTENANCE = '832',
        SAFETY = '834',
        TRAININGCHECKLIST = '837',
        PREINSTALLATION_REPORT = '311',
        INSTALLATION_REPORT = '312',
        INSTALLATION_STARTUP_REPORT = '314',
        STARTUP_REPORT = '315',
        SOFTWARE_HANDLING_REPORT = '316',
        TUNE_UP_REPORT = '317',
        QUALITY_ASSURANCE_REPORT = '320',
        DISASSEMBLY_REPORT = '321',
        RECONDITIONING_REPORT = '322',
        CUSTOMER_ACCEPTANCE_REPORT = '323',
        COMMISSIONING_REPORT = '324',
        MAINTENANCE_REPORT = '331', // replaces 832
        SAFETY_RELATED_TEST_REPORT = '333', // replaces 834
        APPLICATION_TRAINING_REPORT = '336', // replaces 837 - training checklist
        PRE_TRAINING_CLARIFICATION_REPORT = '338',
        TROUBLESHOOTING_REPORT = '340',
        REPLACEMENT_REPORT = '341',
        ADJUSTMENT_REPORT = '342',
        CONFIGURATION_REPORT = '343',
        SAFETY_INFORMATION_REPORT = '360',
        DISPOSAL_REPORT = '361',
        SITE_PLANNING_REPORT = '391',
        UPGRADE_REPORT = '392',
        UPDATE_REPORT = '396',
        UNKNOWN = '',
    }

    export const FORMS_INSTRUCTIONS_MAPPING: Readonly<{ [key: string]: string }> = {
        '832': '831',
        '834': '833',
        '837': '836',
        '311': '811',
        '312': '812',
        '314': '814',
        '315': '815',
        '316': '816',
        '317': '817',
        '320': '820',
        '321': '821',
        '322': '822',
        '323': '823',
        '324': '824',
        '331': '831',
        '333': '833',
        '336': '836',
        '338': '838',
        '340': '840',
        '341': '841',
        '342': '842',
        '343': '843',
        '360': '860',
        '361': '861',
        '391': '891',
        '392': '892',
        '396': '896',
    };

    export const DOCBOX_TECHNICAL_NAME: Readonly<{ [key: string]: string }> = {
        '311': 'pre_installation_report',
        '312': 'installation_report',
        '314': 'installation_and_startup_report',
        '315': 'startup_report',
        '316': 'software_handling_report',
        '317': 'tune_up_report',
        '320': 'quality_assurance_report',
        '321': 'disassembly_report',
        '322': 'reconditioning_report',
        '323': 'customer_acceptance',
        '324': 'commissioning',
        '361': 'disposal_report',
        '392': 'upgrade_report',
    };

    export const ASTORIA_FORMTYPES: readonly FORMTYPE[] = [
        FORMTYPE.MAINTENANCE,
        FORMTYPE.SAFETY,
        FORMTYPE.TRAININGCHECKLIST,
    ] as const;

    export const COSIMA_FORMTYPES: readonly FORMTYPE[] = [
        FORMTYPE.PREINSTALLATION_REPORT,
        FORMTYPE.INSTALLATION_REPORT,
        FORMTYPE.INSTALLATION_STARTUP_REPORT,
        FORMTYPE.STARTUP_REPORT,
        FORMTYPE.SOFTWARE_HANDLING_REPORT,
        FORMTYPE.TUNE_UP_REPORT,
        FORMTYPE.QUALITY_ASSURANCE_REPORT,
        FORMTYPE.DISASSEMBLY_REPORT,
        FORMTYPE.RECONDITIONING_REPORT,
        FORMTYPE.MAINTENANCE_REPORT,
        FORMTYPE.SAFETY_RELATED_TEST_REPORT,
        FORMTYPE.APPLICATION_TRAINING_REPORT,
        FORMTYPE.TROUBLESHOOTING_REPORT,
        FORMTYPE.REPLACEMENT_REPORT,
        FORMTYPE.ADJUSTMENT_REPORT,
        FORMTYPE.CONFIGURATION_REPORT,
        FORMTYPE.SAFETY_INFORMATION_REPORT,
        FORMTYPE.DISPOSAL_REPORT,
        FORMTYPE.SITE_PLANNING_REPORT,
        FORMTYPE.UPGRADE_REPORT,
        FORMTYPE.UPDATE_REPORT,
        FORMTYPE.CUSTOMER_ACCEPTANCE_REPORT,
        FORMTYPE.COMMISSIONING_REPORT,
        FORMTYPE.PRE_TRAINING_CLARIFICATION_REPORT,
    ] as const;

    export const COSIMA_INSTALLATION_FORMTYPES: readonly FORMTYPE[] = [
        FORMTYPE.PREINSTALLATION_REPORT,
        FORMTYPE.INSTALLATION_REPORT,
        FORMTYPE.INSTALLATION_STARTUP_REPORT,
        FORMTYPE.STARTUP_REPORT,
        FORMTYPE.SOFTWARE_HANDLING_REPORT,
        FORMTYPE.TUNE_UP_REPORT,
        FORMTYPE.QUALITY_ASSURANCE_REPORT,
        FORMTYPE.DISASSEMBLY_REPORT,
        FORMTYPE.RECONDITIONING_REPORT,
        FORMTYPE.COMMISSIONING_REPORT,
        FORMTYPE.CUSTOMER_ACCEPTANCE_REPORT,
        FORMTYPE.DISPOSAL_REPORT,
        FORMTYPE.UPGRADE_REPORT,
    ] as const;

    export enum INFOCLASS {
        INSTALLATION = 'INSTALLATION',
        // Infoclass 'APPLICATION' should be used for training checklists
        APPLICATION = 'APPLICATION',
        UNKNOWN = '',
    }

    export enum FORMSTATUS {
        LOCK = 'LOCK',
        PREVIEW = 'PREW',
        UNKNOWN = '',
    }

    export enum READONLY_REASON {
        NONE = 'NONE', // There is no reason to enable read-only mode, so the form is editable
        OSR = 'OSR', // The notification is not in state OSR, so the form should be be edited yet
        LOCK = 'LOCK', // The user does not have the lock
        FINAL = 'FINAL', // The form was previously submitted as final report
        INTERIM = 'INTERIM', // The form was previously submitted as interim report
        PREVIEW = 'PREVIEW', // The form was opened in preview mode from the form list
        HANDOVER = 'HANDOVER', // The form is locked when Handover to another user
        NOT_LOCKED = 'NOT_LOCKED', // The form have lockedBy as empty or undefined
        REFRESHED_FORM_OLD_VERSION = 'REFRESHED_FORM_OLD_VERSION', // The form has new version available and xml is refreshed
        MANUAL_INDIVIDUALIZATION = 'MANUAL_INDIVIDUALIZATION', // The form is locked when manual individualzation is enabled
        PREVIOUS_SUBMIT = 'PREVIOUS_SUBMIT', // The form is locked when form is previously submitted by another device
        SUBMITTED_FROM_ANOTHER_TAB = 'SUBMITTED_FROM_ANOTHER_TAB', // The form is locked when form is previously submitted from another tab
        ABORTED_FROM_ANOTHER_TAB = 'ABORTED_FROM_ANOTHER_TAB', // The form is locked when form is previously aborted from another tab
        HANDOVER_FROM_ANOTHER_TAB = 'HANDOVER_FROM_ANOTHER_TAB', // The form is locked when form is previously handover from another tab
    }

    /**
     * checkpoint connections needs to have these radio button labels set because they aren't part of the form's xml
     * keys: PDFForm->TYPE attribute values
     * values: default values, otherwise set from F4M_GetCustomXmlSet service
     */
    export enum RADIOBUTTON_LABELS {
        YES = 'OK',
        NO = 'not OK',
        NA = 'n.a.',
    }

    export const NA = 'N.A.';

    /**
     * needed by the backend to identify the save mode
     */
    export enum LOAD_STATUS {
        INTERIM = 'I', // Used on client for forms that were loaded as interim, ie. form with existing content
        TEMPLATE = 'T', // Used on client for forms that were loaded from a template, ie. blank form
    }

    export enum SUBMIT_STATUS {
        UNKNOWN = 'U', // we don't know because we did not check yet
        ABORTED = 'A',
        EDITED = 'E', // opened and edited, but not submitted
        INTERIM = 'I', // submitted as interim, ie. with sections that were either filled completely or were empty
        INTERIM_NOT_OK = 'INO', // submitted as interim with some of the sections filled with "not ok"
        FINAL = 'F', // submitted as final, ie. everything was filled out
        FINAL_NOT_OK = 'FNO', // submitted as final with some of the sections filled with "not ok"
        HANDOVER = 'H', // submitted as handover, ie. without any completeness checks
        REFRESHED = 'R', // status added for frontend use only to mark the forms while refreshing xml
    }

    export enum FORM_LOADING_STATUS {
        LOADED = 'LOADED',
        EDITED = 'EDITED',
        SAVED = 'SAVED',
        UNLOADED = 'UNLOADED',
    }

    /**
     * The values of the type attribute that can be attached to a checkpoint or interactive element.
     * enum values start with '1' to avoid later confusion when we use an OR operator on the element type (which would
     * take the second option for ELEMENT_TYPE.A since it would be 0)
     */
    export enum ELEMENT_TYPE {
        A = 1,
        B,
        C,
        D,
        E,
        F,
        STANDARD,
        QUALITY,
        SAFETY,
        SOFTWARE,
        TIME,
        MATERIAL,
        UNKNOWN,
    }

    export enum MIME_TYPE {
        EPS = 'EPS',
        PNG = 'PNG',
    }

    // The following element types are excluded from the autofill functionality for legal reasons
    export const ELEMENT_TYPES_EXCLUDED_FROM_AUTOFILL = [ELEMENT_TYPE.QUALITY, ELEMENT_TYPE.SAFETY];

    export const MASTER_PARTICIPANT_ID = 'master';

    // This constant is appended to the error message that is thrown when the form could not be parsed. The idea is
    // that the effect that is catching the error is parsing the message and then knows to call releaseLock if the
    // user actually has the lock.
    export const HAS_LOCK = ' (has lock)';

    // This constant represents an empty signature image
    // GW is expecting an empty signature image when no signature is provided from the client.
    export const EMPTY_SIGNATURE =
        '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';

    export const SECTION_TEXT = 'section';

    // These two constants are use for the routing to the confirmation screen
    export const ROUTE_CONFIRMATION = 'main/confirmation';
    export const CONFIRMATION_TYPE_NORMAL = 'N';

    // The suffix to make employee signature entities unique
    export const SYSTEM_CONDITION_EMPLOYEE_SIGNATURE_ENTITY_SUFFIX = '-CSE';
    export const SYSTEM_CONDITION_CUSTOMER_SIGNATURE_ENTITY_SUFFIX = '-CUSTOMER';

    export const INTERIM = 'Interim';
    export const FINAL = 'Final';

    export const INTERNAL = 'Internal';
    export const CUSTOMER_VISIBLE = 'Customer visible';

    export const RESTRICTED = 'restricted';
    export const UNRESTRICTED = 'unrestricted';

    export enum FUNCTION_TYPE {
        OLDER = 'older',
        NEWER = 'newer',
    }

    export enum VISIBILITY {
        YES = 'yes',
        NO = 'no',
    }

    export enum FORM_LANGUAGES {
        German = '01',
        English = '02',
        French = '03',
        Spanish = '04',
        Swedish = '10',
        Italian = '11',
        Russian = '12',
        Danish = '13',
        Norwegian = '14',
        Arabic = '17',
        Flemish = '19',
        Greek = '20',
        'Chinese (standard)' = '21',
        Japanese = '22',
        'Brazilian (Portuguese)' = '23',
        American = '24',
        Hungarian = '25',
        Czech = '26',
        Slovakian = '27',
        Estonian = '28',
        Latvian = '29',
        Lithuanian = '30',
        Slovenian = '31',
        Bulgarian = '32',
        Romanian = '33',
        Turkish = '34',
        Korean = '35',
        Croatian = '36',
        Indonesian = '37',
        Vietnamese = '38',
        Serbian = '39',
        Macedonian = '40',
        Thai = '41',
        'Chinese, traditional' = '42',
        Ukrainian = '43',
        Georgian = '44',
        Bosnian = '45',
        Dutch = '46',
        Montenegrin = '47',
        Armenian = '48',
        Belarusian = '49',
        Kazakh = '50',
        Kyrgyz = '51',
        Polish = '55',
        Finnish = '73',
        Portuguese = '79',
        Multilingual = '99',
    }

    export enum SERVICE_ACTIVITY_TYPES {
        INSTALLATION = 'Installation',
        APPLICATION = 'Application',
        MAINTENANCE = 'Maintenance',
    }

    export const readOnlySubmitStatuses = new Set([
        SUBMIT_STATUS.INTERIM,
        SUBMIT_STATUS.INTERIM_NOT_OK,
        SUBMIT_STATUS.FINAL,
        SUBMIT_STATUS.FINAL_NOT_OK,
        SUBMIT_STATUS.HANDOVER,
    ]);

    export const NO_LOCK_DEVICE_ID = 'NO-LOCK';
}
